import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthPayload, AuthUser } from '../auth/auth.types';
import { CatalogService } from './CatalogService';

const MUST_CHANGE_PASSWORD_KEY = 'must_change_password';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl = environment.apiUrl;

  // Минимальный интервал между silent-refresh'ами /me из interceptor'а.
  // Защищает от «шторма» когда страница делает несколько параллельных
  // запросов и все возвращают 403.
  private static readonly ME_REFRESH_COOLDOWN_MS = 30_000;
  private lastMeAt = 0;

  private sessionCleared = new Subject<void>();
  readonly sessionCleared$ = this.sessionCleared.asObservable();

  private readonly mustChangePasswordSubject = new BehaviorSubject<boolean>(this.readMustChangePassword());
  /** Обязательная смена пароля: пока true, в админке доступен только «Мой профиль». */
  readonly mustChangePassword$ = this.mustChangePasswordSubject.asObservable();

  constructor(
    private http: HttpClient,
    private catalogService: CatalogService
  ) {}

  login(username: string, password: string): Observable<AuthPayload> {
    return this.http.post<AuthPayload>(`${this.baseUrl}/login`, { username, password });
  }

  getMe(): Observable<AuthPayload> {
    return this.http
      .get<AuthPayload>(`${this.baseUrl}/me`, { headers: this.getHttpHeaders() })
      .pipe(tap(() => this.lastMeAt = Date.now()));
  }

  /**
   * Тихая синхронизация прав. Используется interceptor'ом после 403.
   * Не подписываемся снаружи — fire-and-forget. HTTP-стрим завершится
   * сам, подписка не требует cleanup.
   */
  refreshIfStale(): void {
    const now = Date.now();
    if (now - this.lastMeAt < AuthService.ME_REFRESH_COOLDOWN_MS) return;
    this.lastMeAt = now; // оптимистично, чтобы при сетевой ошибке не ретраить
    this.getMe().subscribe({
      next: payload => this.setSession(payload, false),
      error: () => { /* auth-init.factory подхватит на следующем bootstrap'е */ }
    });
  }

  setSession(payload: AuthPayload, rememberMe: boolean = false): void {
    const currentIdentity = this.currentSessionIdentity();
    const nextIdentity = this.sessionIdentity(payload);
    if (currentIdentity !== nextIdentity) {
      this.catalogService.clearCache();
      this.sessionCleared.next();
    }

    if (payload.access_token) {
      sessionStorage.setItem('access_token', payload.access_token);
      if (rememberMe) {
        const maxAge = 7 * 24 * 60 * 60;
        document.cookie = `access_token=${payload.access_token}; path=/; Max-Age=${maxAge}`;
      }
    }

    // Single source of truth: всё кроме токена живёт только в sessionStorage.
    // Старый cookie user_role подчищаем, чтобы не было устаревшего источника.
    document.cookie = `user_role=; Max-Age=0; path=/;`;

    sessionStorage.setItem('user_role', payload.role ?? '');
    sessionStorage.setItem('roles', JSON.stringify(payload.roles ?? []));
    sessionStorage.setItem('permissions', JSON.stringify(payload.permissions ?? []));
    sessionStorage.setItem('is_super_admin', String(!!payload.is_super_admin));
    sessionStorage.setItem('is_corp', String(!!payload.is_corp));
    sessionStorage.setItem('corp_discount_percent', String(payload.corp_discount_percent ?? 0));
    if (payload.user) {
      sessionStorage.setItem('user', JSON.stringify(payload.user));
    }
    this.setMustChangePassword(!!payload.must_change_password);
  }

  mustChangePassword(): boolean {
    return this.mustChangePasswordSubject.value;
  }

  setMustChangePassword(value: boolean): void {
    sessionStorage.setItem(MUST_CHANGE_PASSWORD_KEY, String(value));
    if (this.mustChangePasswordSubject.value !== value) {
      this.mustChangePasswordSubject.next(value);
    }
  }

  private readMustChangePassword(): boolean {
    return sessionStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === 'true';
  }

  clearSession(): void {
    this.catalogService.clearCache();
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('roles');
    sessionStorage.removeItem('permissions');
    sessionStorage.removeItem('is_super_admin');
    sessionStorage.removeItem('is_corp');
    sessionStorage.removeItem('corp_discount_percent');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
    this.mustChangePasswordSubject.next(false);
    document.cookie = `access_token=; Max-Age=0; path=/;`;
    document.cookie = `user_role=; Max-Age=0; path=/;`;
    this.sessionCleared.next();
  }

  getUser(): AuthUser | null {
    return this.safeParse<AuthUser | null>(sessionStorage.getItem('user'), null);
  }

  getRoles(): string[] {
    return this.safeParse<string[]>(sessionStorage.getItem('roles'), []);
  }

  getPermissions(): string[] {
    return this.safeParse<string[]>(sessionStorage.getItem('permissions'), []);
  }

  private safeParse<T>(raw: string | null, fallback: T): T {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  private currentSessionIdentity(): string {
    const user = this.getUser();
    return [
      user?.id ?? user?.email ?? user?.username ?? '',
      sessionStorage.getItem('is_corp') ?? '',
      sessionStorage.getItem('user_role') ?? ''
    ].join('|');
  }

  private sessionIdentity(payload: AuthPayload): string {
    return [
      payload.user?.id ?? payload.user?.email ?? payload.user?.username ?? '',
      String(!!payload.is_corp),
      payload.role ?? ''
    ].join('|');
  }

  isSuperAdmin(): boolean {
    return sessionStorage.getItem('is_super_admin') === 'true';
  }

  hasPermission(permission: string): boolean {
    if (this.isSuperAdmin()) return true;
    return this.getPermissions().includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (this.isSuperAdmin()) return true;
    const granted = this.getPermissions();
    return permissions.some(p => granted.includes(p));
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  /** Сотрудник: любая роль, кроме client, или супер-админ (как IsStaff на бэке). */
  isStaff(): boolean {
    if (!this.isLoggedIn()) return false;
    if (this.isSuperAdmin()) return true;
    return this.getRoles().some(role => role !== 'client');
  }

  isCorp(): boolean {
    return sessionStorage.getItem('is_corp') === 'true';
  }

  getCorpDiscountPercent(): number {
    const raw = sessionStorage.getItem('corp_discount_percent');
    return raw ? parseFloat(raw) : 0;
  }

  // --- legacy: оставлены для совместимости с guard'ами и logout-handlers ---
  // Удалить в Фазе 2 после рефакторинга admin/client guard'ов и logout.

  setToken(token: string, rememberMe: boolean = false): void {
    sessionStorage.setItem('access_token', token);
    if (rememberMe) {
      const maxAge = 7 * 24 * 60 * 60;
      document.cookie = `access_token=${token}; path=/; Max-Age=${maxAge}`;
    }
  }

  setRole(role: string, rememberMe: boolean = false): void {
    sessionStorage.setItem('user_role', role);
    // Cookie больше не пишем — единственный источник правды теперь /me.
    // Параметр rememberMe оставлен для обратной совместимости сигнатуры.
    void rememberMe;
  }

  getRole(): string | null {
    const sessionRole = sessionStorage.getItem('user_role');
    if (sessionRole) return sessionRole;
    const match = document.cookie.match(new RegExp('(^| )user_role=([^;]+)'));
    const cookieRole = match ? match[2] : null;
    if (cookieRole) {
      sessionStorage.setItem('user_role', cookieRole);
      return cookieRole;
    }
    return null;
  }

  clearToken(): void {
    this.clearSession();
  }

  // --- /legacy ---

  getToken(): string | null {
    const sessionToken = sessionStorage.getItem('access_token');
    if (sessionToken) return sessionToken;
    const match = document.cookie.match(new RegExp('(^| )access_token=([^;]+)'));
    return match ? match[2] : null;
  }

  getHttpHeaders(): HttpHeaders {
    const token = this.getToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  logout(): Observable<any> {
    return this.http.post(`${this.baseUrl}/logout`, {}, { headers: this.getHttpHeaders() });
  }

  isLoggedIn(): boolean {
    return this.getToken() !== null;
  }

  getUserByContragent(contragentId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/contragents/${contragentId}/user`, { headers: this.getHttpHeaders() });
  }

  validateToken(token: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${this.baseUrl}/validate-token`, { headers });
  }

  updateUser(userId: number, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/users/${userId}`, data, { headers: this.getHttpHeaders() });
  }

  changePassword(data: { current_password: string; new_password: string; new_password_confirmation: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/user/change-password`, data, { headers: this.getHttpHeaders() });
  }

  /** Сброс пароля клиенту: временный пароль генерирует сервер и отдаёт один раз; при входе клиент обязан его сменить. */
  resetUserPassword(userId: number): Observable<{ message: string; temporary_password: string }> {
    return this.http.post<{ message: string; temporary_password: string }>(
      `${this.baseUrl}/users/${userId}/reset-password`, {}, { headers: this.getHttpHeaders() });
  }
}
